import { translations } from "@/lib/i18n";
import { type Locale, resolveLocaleForPath } from "@/lib/i18n/locale";
import { SITE_PAGES, type SitePage } from "@/lib/site-pages";
import { USE_CASES, type UseCaseKey } from "@/lib/use-cases";
import {
	buildBreadcrumbStructuredData,
	buildCanonicalUrl,
	buildFaqStructuredData,
	buildSeo,
	SITE_NAME,
	SITE_URL,
	type StructuredData,
} from "./seo";

type FaqItems = Record<string, { question: string; answer: string }>;

/** Loader shared by static pages: they only need the resolved locale. */
export const loadPageLocale = ({
	location,
	serverContext,
}: {
	location: { pathname: string };
	serverContext?: unknown;
}) => ({ locale: resolveLocaleForPath(location.pathname, serverContext) });

export const buildPageSeo = (
	page: SitePage,
	locale: Locale,
	options: {
		structuredData?: StructuredData[];
		agentReadable?: { markdownUrl: string; textUrl: string };
	} = {},
) => {
	const copy = translations[locale].seo.pages[page];
	return buildSeo({
		title: copy.title,
		description: copy.description,
		path: SITE_PAGES[page],
		locale,
		...options,
	});
};

export const buildPageBreadcrumb = (locale: Locale, trail: SitePage[]) =>
	buildBreadcrumbStructuredData(
		(["home", ...trail] as SitePage[]).map((page) => ({
			name: translations[locale].seo.pages[page].name,
			url: buildCanonicalUrl(SITE_PAGES[page], locale),
		})),
	);

export const buildServiceStructuredData = (page: SitePage, locale: Locale) => {
	const copy = translations[locale].seo.pages[page];
	return {
		"@context": "https://schema.org",
		"@type": "Service",
		name: copy.title,
		description: copy.description,
		url: buildCanonicalUrl(SITE_PAGES[page], locale),
		provider: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
	};
};

export const buildPageFaq = (items: FaqItems) =>
	buildFaqStructuredData(Object.values(items));

/** Head of a use-case page: SEO, service, breadcrumb and FAQ structured data. */
export const buildUseCaseHead = (useCase: UseCaseKey, locale: Locale) => {
	const { page } = USE_CASES[useCase];
	return buildPageSeo(page, locale, {
		structuredData: [
			buildServiceStructuredData(page, locale),
			buildPageBreadcrumb(locale, ["useCases", page]),
			buildPageFaq(translations[locale].useCases.items[useCase].faq.items),
		],
	});
};
